import { useMemo } from 'react';
import { ShieldOff, TrendingDown, TrendingUp } from 'lucide-react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { ApiError } from '@/core/api';
import { formatMoney } from '@/lib/money';
import { usePnl, useRevenueByClient } from '../queries';

function fmt(n: number | string): string {
  return formatMoney(String(n), 'USD', { narrowSymbol: true }) ?? '—';
}

export function FinanceReportsPage() {
  const pnl = usePnl();
  const revenue = useRevenueByClient();

  // Either endpoint returning 403 means the role lacks finance access.
  const blocked =
    (pnl.isError && (pnl.error as unknown as ApiError)?.status === 403) ||
    (revenue.isError && (revenue.error as unknown as ApiError)?.status === 403);

  const totalRevenue = useMemo(
    () =>
      (revenue.data ?? []).reduce((sum, row) => sum + (Number(row.revenue_base_usd) || 0), 0),
    [revenue.data],
  );

  if (blocked) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold tracking-tight">Finance Reports</h1>
        <div className="text-muted-foreground flex flex-col items-center gap-2 rounded-md border border-dashed py-12 text-sm">
          <ShieldOff className="size-6" />
          Finance role required to view P&amp;L and revenue reports.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Finance Reports</h1>
        <p className="text-muted-foreground text-sm">
          P&amp;L and revenue by client. All figures in base USD.
        </p>
      </div>

      {pnl.isError && !blocked ? (
        <p className="text-destructive text-sm">
          {(pnl.error as unknown as ApiError)?.message ?? 'Failed to load P&L.'}
        </p>
      ) : (
        <div className="grid gap-4 sm:grid-cols-4">
          <Card>
            <CardHeader>
              <CardDescription>Income</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {pnl.isPending ? <Skeleton className="h-7 w-24" /> : fmt(pnl.data!.income_base_usd)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>Expenses</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {pnl.isPending ? <Skeleton className="h-7 w-24" /> : fmt(pnl.data!.expense_base_usd)}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>Net</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {pnl.isPending ? (
                  <Skeleton className="h-7 w-24" />
                ) : (
                  <span
                    className={
                      Number(pnl.data!.net_base_usd) >= 0 ? 'text-emerald-600' : 'text-destructive'
                    }
                  >
                    {fmt(pnl.data!.net_base_usd)}
                  </span>
                )}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardDescription>MRR estimate</CardDescription>
              <CardTitle className="text-2xl tabular-nums">
                {pnl.isPending ? (
                  <Skeleton className="h-7 w-24" />
                ) : (
                  fmt(pnl.data!.mrr_estimate_base_usd)
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="text-muted-foreground text-xs">
              Trailing 3-month average income.
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Monthly P&amp;L</CardTitle>
          <CardDescription>Income, expense, net by month (base USD).</CardDescription>
        </CardHeader>
        <CardContent>
          {pnl.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : pnl.data && pnl.data.monthly.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Month</TableHead>
                  <TableHead className="text-right">Income</TableHead>
                  <TableHead className="text-right">Expense</TableHead>
                  <TableHead className="text-right">Net</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pnl.data.monthly.map((m) => {
                  const net = Number(m.net);
                  return (
                    <TableRow key={m.month}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(m.month).toLocaleDateString(undefined, {
                          year: 'numeric',
                          month: 'short',
                        })}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(m.income)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(m.expense)}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        <span
                          className={
                            net >= 0
                              ? 'text-emerald-600 inline-flex items-center gap-1'
                              : 'text-destructive inline-flex items-center gap-1'
                          }
                        >
                          {net >= 0 ? (
                            <TrendingUp className="size-3.5" />
                          ) : (
                            <TrendingDown className="size-3.5" />
                          )}
                          {fmt(net)}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No P&amp;L history yet.</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Revenue by Client</CardTitle>
          <CardDescription>
            Recognized income grouped by client company (base USD).{' '}
            <a
              href="/api/command-center/reports/revenue-by-client/?format=csv"
              className="text-primary hover:underline"
            >
              Download CSV
            </a>
          </CardDescription>
        </CardHeader>
        <CardContent>
          {revenue.isPending ? (
            <Skeleton className="h-24 w-full" />
          ) : revenue.isError ? (
            <p className="text-destructive text-sm">
              {(revenue.error as unknown as ApiError)?.message ?? 'Failed to load revenue.'}
            </p>
          ) : revenue.data && revenue.data.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Company</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">% of total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {revenue.data.map((row) => {
                  const r = Number(row.revenue_base_usd) || 0;
                  const pct = totalRevenue > 0 ? Math.round((r / totalRevenue) * 100) : 0;
                  return (
                    <TableRow key={row.company}>
                      <TableCell className="font-medium">{row.company}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmt(r)}</TableCell>
                      <TableCell className="text-muted-foreground text-right tabular-nums">
                        {pct}%
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <p className="text-muted-foreground text-sm">No revenue recorded yet.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
